import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  ChevronLeft, Undo2, Redo2, Eye, Send, Save, X, Plus, Trash2, Copy,
  ArrowUp, ArrowDown, LayoutGrid, Loader2, Upload, Settings as SettingsIcon,
  Users, Sparkles, Bookmark, LayoutTemplate,
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import RichTextEditor from "./RichTextEditor";
import AIEmailDesigner from "./AIEmailDesigner";
import { marketingApi, platformApi } from "../../lib/api";
import { sanitizeHtml } from "../../lib/sanitize";
import {
  BLOCK_DEFS, LAYOUTS, MERGE_TAGS, MERGE_TAG_GROUPS, makeBlock, makeRow, defaultDesign,
  renderBlock, renderEmailHtml, fillSample, getSections, getTemplates, absUrl,
} from "./emailBlocks";
import { POPUP_BLOCK_DEFS, POPUP_BLOCK_TYPES, makePopupBlock, renderPopupBlockPreview } from "./popupBlocks";

// Renders a block for canvas/iframe preview, substituting the popup-only
// blocks' static preview markup since emailBlocks.jsx doesn't know about them.
function renderBlockPreview(b) {
  return POPUP_BLOCK_TYPES.has(b.type) ? renderPopupBlockPreview(b) : renderBlock(b);
}

// Mirrors renderEmailHtml()'s row/table structure but routes each block
// through renderBlockPreview() above — used only when popupMode is on, so
// the shared emailBlocks.jsx renderer is never modified.
function renderPopupHtml(design) {
  const s = design.settings || {};
  const rowsHtml = (design.rows || []).map((row) => {
    const w = (100 / row.columns.length).toFixed(4);
    const cells = row.columns.map((col) =>
      `<td valign="top" width="${w}%" style="padding:6px 10px;">${(col || []).map(renderBlockPreview).join("")}</td>`
    ).join("");
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${row.bg || "transparent"};"><tr>${cells}</tr></table>`;
  }).join("");
  const width = s.contentWidth || 600;
  return `<div style="background:${s.backgroundColor || "#f1f5f9"};padding:24px 8px;font-family:${s.fontFamily || "Helvetica, Arial, sans-serif"};">` +
    `<table role="presentation" align="center" width="${width}" cellpadding="0" cellspacing="0" style="width:100%;max-width:${width}px;margin:0 auto;background:${s.contentBackgroundColor || "#ffffff"};border-radius:10px;overflow:hidden;">` +
    `<tr><td style="padding:18px 14px;">${rowsHtml}</td></tr></table></div>`;
}

const ALIGN = ["left", "center", "right"];
const TYPES = ["Residential", "Commercial"];
const MEMBERSHIPS = ["Silver", "Gold", "Platinum"];

/* ---- tiny inputs ---- */
const L = ({ children }) => <label className="text-[11px] font-medium text-slate-500">{children}</label>;
function Field({ label, children }) { return <div><L>{label}</L><div className="mt-1">{children}</div></div>; }
function Color({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-9 w-11 shrink-0 rounded-md border border-slate-200 cursor-pointer bg-white p-0.5" />
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1 min-w-0 h-9 px-2 rounded-md border border-slate-200 text-[12.5px] focus:outline-none" />
    </div>
  );
}
function AlignPicker({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {ALIGN.map((a) => (
        <button key={a} onClick={() => onChange(a)} data-testid={`align-${a}`}
          className={`flex-1 h-8 rounded-md text-[12px] capitalize border ${value === a ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{a}</button>
      ))}
    </div>
  );
}
const txtInput = "w-full h-8 px-2.5 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20";

function AudienceChip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`h-8 px-3 rounded-full text-[12px] font-medium border transition-colors ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{children}</button>
  );
}

/* ---- block settings ---- */
function BlockSettings({ block, accent, onChange, onUpload }) {
  const set = (patch) => onChange({ ...block, ...patch });
  switch (block.type) {
    case "headline":
      return (
        <div className="space-y-3" data-testid="bs-headline">
          <Field label="Text"><input value={block.text} onChange={(e) => set({ text: e.target.value })} data-testid="bs-text" className={txtInput} /></Field>
          <div className="flex flex-wrap gap-1">{MERGE_TAGS.map((t) => <button key={t} onClick={() => set({ text: block.text + `{{${t}}}` })} className="h-6 px-1.5 rounded text-[10.5px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100">{`{{${t}}}`}</button>)}</div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Font size"><input type="number" value={block.fontSize} onChange={(e) => set({ fontSize: Number(e.target.value) })} className={txtInput} /></Field>
            <Field label="Color"><Color value={block.color} onChange={(c) => set({ color: c })} /></Field>
          </div>
          <Field label="Align"><AlignPicker value={block.align} onChange={(a) => set({ align: a })} /></Field>
          <label className="flex items-center gap-2 text-[13px] text-slate-600"><input type="checkbox" checked={block.bold} onChange={(e) => set({ bold: e.target.checked })} className="accent-blue-600" /> Bold</label>
        </div>
      );
    case "paragraph":
      return (
        <div className="space-y-3" data-testid="bs-paragraph">
          <Field label="Text"><RichTextEditor value={block.html} onChange={(html) => set({ html })} mergeTags={MERGE_TAG_GROUPS} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Font size"><input type="number" value={block.fontSize} onChange={(e) => set({ fontSize: Number(e.target.value) })} className={txtInput} /></Field>
            <Field label="Color"><Color value={block.color} onChange={(c) => set({ color: c })} /></Field>
          </div>
          <Field label="Align"><AlignPicker value={block.align} onChange={(a) => set({ align: a })} /></Field>
        </div>
      );
    case "image":
      return (
        <div className="space-y-3" data-testid="bs-image">
          <Field label="Image">
            <div className="flex gap-2">
              <input value={block.src} onChange={(e) => set({ src: e.target.value })} placeholder="Image URL" className={txtInput} />
              <button onClick={() => onUpload((url) => set({ src: url }))} data-testid="bs-image-upload" className="h-8 px-2.5 rounded-md text-[12px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1 shrink-0"><Upload className="h-3.5 w-3.5" /> Upload</button>
            </div>
          </Field>
          <Field label="Alt text"><input value={block.alt} onChange={(e) => set({ alt: e.target.value })} className={txtInput} /></Field>
          <Field label="Link (optional)"><input value={block.href} onChange={(e) => set({ href: e.target.value })} placeholder="https://" className={txtInput} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Width %"><input type="number" min="10" max="100" value={block.width} onChange={(e) => set({ width: Number(e.target.value) })} className={txtInput} /></Field>
            <Field label="Align"><AlignPicker value={block.align} onChange={(a) => set({ align: a })} /></Field>
          </div>
        </div>
      );
    case "button":
      return (
        <div className="space-y-3" data-testid="bs-button">
          <Field label="Label"><input value={block.text} onChange={(e) => set({ text: e.target.value })} className={txtInput} /></Field>
          <Field label="Link"><input value={block.href} onChange={(e) => set({ href: e.target.value })} placeholder="https://" className={txtInput} /></Field>
          <div className="flex flex-wrap gap-1">{["reviewLink"].map((t) => <button key={t} onClick={() => set({ href: `{{${t}}}` })} className="h-6 px-1.5 rounded text-[10.5px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100">{`{{${t}}}`}</button>)}</div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Background"><Color value={block.bgColor} onChange={(c) => set({ bgColor: c })} /></Field>
            <Field label="Text color"><Color value={block.color} onChange={(c) => set({ color: c })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Radius"><input type="number" value={block.radius} onChange={(e) => set({ radius: Number(e.target.value) })} className={txtInput} /></Field>
            <Field label="Align"><AlignPicker value={block.align} onChange={(a) => set({ align: a })} /></Field>
          </div>
        </div>
      );
    case "divider":
      return (
        <div className="space-y-3" data-testid="bs-divider">
          <Field label="Color"><Color value={block.color} onChange={(c) => set({ color: c })} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Thickness"><input type="number" value={block.thickness} onChange={(e) => set({ thickness: Number(e.target.value) })} className={txtInput} /></Field>
            <Field label="Padding"><input type="number" value={block.padding} onChange={(e) => set({ padding: Number(e.target.value) })} className={txtInput} /></Field>
          </div>
        </div>
      );
    case "spacer":
      return <div data-testid="bs-spacer"><Field label="Height (px)"><input type="number" value={block.height} onChange={(e) => set({ height: Number(e.target.value) })} className={txtInput} /></Field></div>;
    case "social":
      return (
        <div className="space-y-2" data-testid="bs-social">
          {(block.links || []).map((lk, i) => (
            <div key={i} className="flex gap-1.5">
              <select value={lk.network} onChange={(e) => { const links = [...block.links]; links[i] = { ...lk, network: e.target.value }; set({ links }); }} className="h-8 px-1.5 rounded-md border border-slate-200 text-[12px] bg-white">
                {["facebook", "instagram", "x", "linkedin", "youtube"].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input value={lk.url} onChange={(e) => { const links = [...block.links]; links[i] = { ...lk, url: e.target.value }; set({ links }); }} placeholder="https://" className={txtInput} />
              <button onClick={() => set({ links: block.links.filter((_, j) => j !== i) })} className="h-8 w-8 rounded-md text-red-400 hover:bg-red-50 flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <button onClick={() => set({ links: [...(block.links || []), { network: "facebook", url: "" }] })} className="h-8 px-2.5 rounded-md text-[12px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add link</button>
          <Field label="Align"><AlignPicker value={block.align} onChange={(a) => set({ align: a })} /></Field>
        </div>
      );
    case "video":
      return (
        <div className="space-y-3" data-testid="bs-video">
          <Field label="Thumbnail">
            <div className="flex gap-2">
              <input value={block.thumbnailSrc} onChange={(e) => set({ thumbnailSrc: e.target.value })} placeholder="Image URL" className={txtInput} />
              <button onClick={() => onUpload((url) => set({ thumbnailSrc: url }))} className="h-8 px-2.5 rounded-md text-[12px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1 shrink-0"><Upload className="h-3.5 w-3.5" /> Upload</button>
            </div>
          </Field>
          <Field label="Video link"><input value={block.href} onChange={(e) => set({ href: e.target.value })} placeholder="https://youtube.com/..." className={txtInput} /></Field>
        </div>
      );
    case "html":
      return <div data-testid="bs-html"><Field label="Custom HTML"><textarea value={block.code} onChange={(e) => set({ code: e.target.value })} rows={8} className="w-full px-2.5 py-2 rounded-md border border-slate-200 text-[12px] font-mono focus:outline-none" /></Field></div>;
    case "promoCode":
      return (
        <div className="space-y-3" data-testid="bs-promo-code">
          <Field label="Label"><input value={block.label} onChange={(e) => set({ label: e.target.value })} className={txtInput} /></Field>
          <Field label="Promo code"><input value={block.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} className={txtInput} /></Field>
          <Field label="Discount %"><input type="number" min="0" max="100" value={block.discountPercent} onChange={(e) => set({ discountPercent: Number(e.target.value) })} className={txtInput} /></Field>
          <Field label="Applies to">
            <select value={block.appliesTo} onChange={(e) => set({ appliesTo: e.target.value })} className={`${txtInput} bg-white`}>
              <option value="credits">AI Credits Top-Up</option>
              <option value="seats">Subscription Seats</option>
              <option value="both">Both</option>
            </select>
          </Field>
        </div>
      );
    case "countdownTimer":
      return (
        <div className="space-y-3" data-testid="bs-countdown-timer">
          <Field label="Label"><input value={block.label} onChange={(e) => set({ label: e.target.value })} className={txtInput} /></Field>
          <Field label="Counts down to"><input type="datetime-local" value={block.targetAt} onChange={(e) => set({ targetAt: e.target.value })} className={txtInput} /></Field>
          <p className="text-[11px] text-slate-400 leading-relaxed">Leave blank to count down to the popup's end date instead.</p>
        </div>
      );
    default:
      return null;
  }
}

/* ---- main builder (full campaign creation surface) ---- */
export default function EmailBuilder({ open, onClose, accentColor, customers, smtpConfigured, defaultTestEmail, onSaved, startWithAI = false, companyName = "", editCampaignId = null, popupMode = false, editPopupId = null }) {
  const accent = accentColor || "#2563EB";
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [design, setDesign] = useState(() => defaultDesign(accent));
  const [crit, setCrit] = useState({ types: [], tags: [], memberships: [], minLtv: 0, servedWithinDays: "", inactiveDays: "" });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sel, setSel] = useState({ kind: "template" });
  const [tab, setTab] = useState("blocks");
  const [propTab, setPropTab] = useState("settings");
  const [customTemplates, setCustomTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [loadingCampaign, setLoadingCampaign] = useState(false);
  const [savingPopup, setSavingPopup] = useState(false);
  const dragRef = useRef(null);
  const uploadCbRef = useRef(null);
  const fileRef = useRef(null);
  const debounceRef = useRef(null);
  const sections = getSections(accent);
  const templates = getTemplates(accent);
  const reviewTemplate = templates.find((t) => t.id === "review");

  const tagOptions = useMemo(() => {
    const s = new Set();
    (customers || []).forEach((c) => (c.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [customers]);

  useEffect(() => {
    if (open) {
      setName(""); setSubject(""); setDesign(defaultDesign(accent));
      setCrit({ types: [], tags: [], memberships: [], minLtv: 0, servedWithinDays: "", inactiveDays: "" });
      setSel({ kind: "template" }); setTab("blocks"); setPropTab("settings"); setPast([]); setFuture([]);
      setPreviewOpen(false); setRecipientsOpen(false); setPreview(null);
      setAiOpen(!!startWithAI && !editCampaignId);  // don't auto-open AI when editing
      marketingApi.listEmailTemplates().then(setCustomTemplates).catch(() => setCustomTemplates([]));
      // Prefill from an existing saved popup (Platform Admin popup builder).
      if (popupMode && editPopupId) {
        setLoadingCampaign(true);
        platformApi.popups.get(editPopupId)
          .then((p) => {
            setName(p.name || "");
            if (p.design) setDesign(p.design);
          })
          .catch(() => toast.error("Could not load popup for editing."))
          .finally(() => setLoadingCampaign(false));
      } else if (editCampaignId) {
        setLoadingCampaign(true);
        marketingApi.getCampaign(editCampaignId)
          .then((c) => {
            setName(c.name || "");
            setSubject(c.subject || "");
            if (c.design) setDesign(c.design);
            const cc = c.criteria || {};
            setCrit({
              types: cc.types || [],
              tags: cc.tags || [],
              memberships: cc.memberships || [],
              minLtv: cc.minLtv || 0,
              servedWithinDays: cc.servedWithinDays ?? "",
              inactiveDays: cc.inactiveDays ?? "",
            });
          })
          .catch(() => toast.error("Could not load campaign for editing."))
          .finally(() => setLoadingCampaign(false));
      }
    }
  }, [open, accent, editCampaignId, editPopupId, popupMode, startWithAI]);

  const refreshCustomTemplates = useCallback(() => {
    marketingApi.listEmailTemplates().then(setCustomTemplates).catch(() => {});
  }, []);

  const saveAsTemplate = async () => {
    if (!design.rows?.length || design.rows.every((r) => r.columns.every((c) => c.length === 0))) {
      return toast.error("Add some content before saving as a template.");
    }
    const tname = window.prompt("Save this design as a reusable template. Template name:", name.trim() || "My template");
    if (tname == null) return;
    if (!tname.trim()) return toast.error("Enter a template name.");
    setSavingTemplate(true);
    try {
      await marketingApi.saveEmailTemplate({ name: tname.trim(), subject: subject.trim(), body: renderEmailHtml(design), design });
      toast.success(`Saved "${tname.trim()}" to your templates`);
      refreshCustomTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save template.");
    } finally { setSavingTemplate(false); }
  };

  const deleteCustomTemplate = async (t) => {
    if (!window.confirm(`Delete the template "${t.name}"?`)) return;
    try {
      await marketingApi.deleteEmailTemplate(t.id);
      setCustomTemplates((list) => list.filter((x) => x.id !== t.id));
      toast.success("Template deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete template.");
    }
  };

  const buildCriteria = useCallback(() => ({
    types: crit.types, tags: crit.tags, memberships: crit.memberships,
    minLtv: Number(crit.minLtv) || 0,
    servedWithinDays: crit.servedWithinDays === "" ? null : Number(crit.servedWithinDays),
    inactiveDays: crit.inactiveDays === "" ? null : Number(crit.inactiveDays),
  }), [crit]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPreviewing(true);
    debounceRef.current = setTimeout(async () => {
      try { setPreview(await marketingApi.previewAudience(buildCriteria())); }
      catch (e) { setPreview(null); }
      finally { setPreviewing(false); }
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [crit, open, buildCriteria]);

  const snapshot = useCallback(() => { setPast((p) => [...p.slice(-49), design]); setFuture([]); }, [design]);
  const mutate = (fn) => { snapshot(); setDesign((d) => fn(structuredClone(d))); };
  const edit = (fn) => setDesign((d) => fn(structuredClone(d)));
  const undo = () => { if (!past.length) return; setFuture((f) => [design, ...f]); setDesign(past[past.length - 1]); setPast((p) => p.slice(0, -1)); };
  const redo = () => { if (!future.length) return; setPast((p) => [...p, design]); setDesign(future[0]); setFuture((f) => f.slice(1)); };

  const applyAIDesign = (res) => {
    if (!res?.design) return;
    setPast((p) => [...p.slice(-49), design]); setFuture([]);
    setDesign(structuredClone(res.design));
    setName((n) => n || res.name || "");
    setSubject(res.subject || "");
    if (res.design.settings?.preheader != null) { /* preheader already in design.settings */ }
    setSel({ kind: "template" }); setPropTab("template");
  };

  if (!open) return null;

  const findBlock = (d, rowId, col, blockId) => (d.rows.find((r) => r.id === rowId)?.columns[col] || []).find((b) => b.id === blockId);

  const createBlock = (type) => (POPUP_BLOCK_TYPES.has(type) ? makePopupBlock(type) : makeBlock(type, accent));

  const addBlock = (type) => {
    const block = createBlock(type);
    mutate((d) => {
      let rowId, col;
      if (sel.kind === "block") { rowId = sel.rowId; col = sel.colIndex; }
      else if (sel.kind === "row") { rowId = sel.rowId; col = 0; }
      else if (d.rows.length) { rowId = d.rows[d.rows.length - 1].id; col = 0; }
      else { const r = makeRow(1); d.rows.push(r); rowId = r.id; col = 0; }
      d.rows.find((r) => r.id === rowId).columns[col].push(block);
      return d;
    });
    setSel({ kind: "block", rowId: sel.rowId || (design.rows[design.rows.length - 1]?.id), colIndex: sel.colIndex || 0, blockId: block.id });
    setPropTab("settings");
  };

  const dropBlockInColumn = (rowId, col) => {
    const d = dragRef.current; dragRef.current = null;
    if (!d) return;
    if (d.kind === "newblock") {
      const block = createBlock(d.type);
      mutate((dd) => { dd.rows.find((r) => r.id === rowId).columns[col].push(block); return dd; });
      setSel({ kind: "block", rowId, colIndex: col, blockId: block.id });
    } else if (d.kind === "moveblock") {
      mutate((dd) => {
        const from = dd.rows.find((r) => r.id === d.rowId);
        const idx = from.columns[d.col].findIndex((b) => b.id === d.blockId);
        if (idx < 0) return dd;
        const [b] = from.columns[d.col].splice(idx, 1);
        dd.rows.find((r) => r.id === rowId).columns[col].push(b);
        return dd;
      });
    }
  };

  const addRow = (cols) => { const r = makeRow(cols); mutate((d) => { d.rows.push(r); return d; }); setSel({ kind: "row", rowId: r.id }); setPropTab("settings"); };
  const addSection = (s) => { const rows = s.rows(); mutate((d) => { d.rows.push(...rows); return d; }); };
  const loadTemplate = (t) => { mutate((d) => { d.settings = structuredClone(t.design.settings); d.rows = structuredClone(t.design.rows); return d; }); if (!subject.trim()) setSubject(t.subject || ""); setSel({ kind: "template" }); toast.success(`Loaded "${t.name}"`); };
  const updateBlock = (rowId, col, blockId, next) => edit((d) => { const arr = d.rows.find((r) => r.id === rowId).columns[col]; const i = arr.findIndex((b) => b.id === blockId); arr[i] = next; return d; });
  const deleteBlock = (rowId, col, blockId) => { mutate((d) => { const arr = d.rows.find((r) => r.id === rowId).columns[col]; arr.splice(arr.findIndex((b) => b.id === blockId), 1); return d; }); setSel({ kind: "template" }); };
  const dupBlock = (rowId, col, blockId) => mutate((d) => { const arr = d.rows.find((r) => r.id === rowId).columns[col]; const i = arr.findIndex((b) => b.id === blockId); arr.splice(i + 1, 0, { ...arr[i], id: `blk-${Math.random().toString(36).slice(2, 10)}` }); return d; });
  const moveBlock = (rowId, col, blockId, dir) => mutate((d) => { const arr = d.rows.find((r) => r.id === rowId).columns[col]; const i = arr.findIndex((b) => b.id === blockId); const j = i + dir; if (j < 0 || j >= arr.length) return d; [arr[i], arr[j]] = [arr[j], arr[i]]; return d; });
  const updateRow = (rowId, patch) => edit((d) => { Object.assign(d.rows.find((r) => r.id === rowId), patch); return d; });
  const deleteRow = (rowId) => { mutate((d) => { d.rows = d.rows.filter((r) => r.id !== rowId); return d; }); setSel({ kind: "template" }); };
  const moveRow = (rowId, dir) => mutate((d) => { const i = d.rows.findIndex((r) => r.id === rowId); const j = i + dir; if (j < 0 || j >= d.rows.length) return d; [d.rows[i], d.rows[j]] = [d.rows[j], d.rows[i]]; return d; });
  const setRowCols = (rowId, cols) => mutate((d) => { const row = d.rows.find((r) => r.id === rowId); const old = row.columns; const next = Array.from({ length: cols }, (_, i) => old[i] || []); if (cols < old.length) next[cols - 1] = next[cols - 1].concat(...old.slice(cols)); row.cols = cols; row.columns = next; return d; });
  const setSettings = (patch) => edit((d) => { Object.assign(d.settings, patch); return d; });

  const openUpload = (cb) => { uploadCbRef.current = cb; fileRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB."); e.target.value = ""; return; }
    try { const res = await marketingApi.uploadAsset(file); uploadCbRef.current?.(absUrl(res.url)); toast.success("Image uploaded"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); }
    e.target.value = "";
  };

  const sendTest = async () => {
    if (!subject.trim()) return toast.error("Add a subject line first.");
    if (!smtpConfigured) return toast.error("Connect your email (SMTP) in Company Settings to send.");
    const to = window.prompt("Send a test email to:", defaultTestEmail || "");
    if (!to) return;
    setTesting(true);
    try { await marketingApi.test({ to, subject, body: renderEmailHtml(design), designed: true }); toast.success(`Test sent to ${to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to send test."); }
    finally { setTesting(false); }
  };

  const save = async (sendNow) => {
    if (!name.trim()) return toast.error("Add a campaign name.");
    if (!subject.trim()) return toast.error("Add a subject line.");
    if (sendNow && !smtpConfigured) return toast.error("Connect your email (SMTP) in Company Settings to send.");
    if (sendNow && (!preview || preview.count === 0)) return toast.error("No customers match this audience.");
    if (sendNow && !window.confirm(`Send this campaign to ${preview.count} customer${preview.count === 1 ? "" : "s"} now?`)) return;
    setSaving(true);
    try {
      let camp;
      if (editCampaignId) {
        // Draft edit — PATCH in place. If Send Now, follow up with send.
        camp = await marketingApi.updateCampaign(editCampaignId, {
          name: name.trim(),
          subject: subject.trim(),
          body: renderEmailHtml(design),
          design,
          designed: true,
          criteria: buildCriteria(),
        });
        if (sendNow) {
          await marketingApi.sendCampaign(editCampaignId);
          camp = { ...camp, status: "sending" };
        }
        toast.success(sendNow ? "Campaign is sending…" : "Draft updated");
      } else {
        camp = await marketingApi.createCampaign({ name: name.trim(), subject: subject.trim(), body: renderEmailHtml(design), design, designed: true, criteria: buildCriteria(), sendNow });
        toast.success(sendNow ? "Campaign is sending…" : "Draft saved");
      }
      onSaved && onSaved(camp);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save campaign.");
    } finally { setSaving(false); }
  };

  const toggleCrit = (key, val) => setCrit((c) => ({ ...c, [key]: c[key].includes(val) ? c[key].filter((x) => x !== val) : [...c[key], val] }));

  const saveAsPopup = async () => {
    const hasContent = (design.rows || []).some((r) => r.columns.some((c) => (c || []).length > 0));
    if (!hasContent) return toast.error("Add some content before saving as a popup.");
    const pname = window.prompt("Popup name:", name.trim() || "New popup");
    if (pname == null) return;
    if (!pname.trim()) return toast.error("Enter a popup name.");
    setSavingPopup(true);
    try {
      let popup;
      if (editPopupId) {
        popup = await platformApi.popups.update(editPopupId, { name: pname.trim(), design });
        toast.success("Popup updated");
      } else {
        popup = await platformApi.popups.create({ name: pname.trim(), design });
        toast.success(`Saved popup "${pname.trim()}"`);
      }
      onSaved && onSaved(popup);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save popup.");
    } finally { setSavingPopup(false); }
  };

  const selBlock = sel.kind === "block" ? findBlock(design, sel.rowId, sel.colIndex, sel.blockId) : null;
  const selRow = sel.kind === "row" ? design.rows.find((r) => r.id === sel.rowId) : null;

  return (
    <div className="fixed inset-0 z-[120] bg-slate-100 flex flex-col" data-testid="email-builder">
      <input type="file" ref={fileRef} accept="image/*" onChange={onFile} className="hidden" />

      {aiOpen && (
        <AIEmailDesigner
          accent={accent}
          companyName={companyName}
          onApplyDesign={applyAIDesign}
          onClose={() => setAiOpen(false)}
        />
      )}

      {/* Floating AI Designer trigger — sits at the bottom-middle of the
       * screen over the dark background area, disappears while the AI panel
       * is open, reappears when it closes. */}
      {!aiOpen && (
        <button
          onClick={() => setAiOpen(true)}
          data-testid="eb-ai-designer"
          className="fixed left-1/2 -translate-x-1/2 bottom-4 z-[110] h-11 px-8 rounded-full text-[14px] font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white transition-shadow"
          style={{ color: "#ffffff" }}
        >
          <Sparkles className="h-4 w-4 shrink-0" color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} />
          <span style={{ color: "#ffffff" }}>AI Designer</span>
        </button>
      )}


      {/* Top bar */}
      <div className="h-14 bg-slate-900 text-white flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onClose}
            data-testid="eb-close"
            title="Back to Marketing"
            style={{ backgroundColor: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.55)", color: "#ffffff" }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.22)"; e.currentTarget.style.borderColor = "#ffffff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.55)"; }}
            className="h-9 w-9 rounded-md flex items-center justify-center shrink-0 transition-colors"
          >
            {/* Inline SVG (not lucide) with width/height forced via style so
                no theme CSS reset can collapse the icon to a 2px sliver. */}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{ width: "20px", height: "20px", minWidth: "20px", minHeight: "20px", display: "block", color: "#ffffff" }}
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="campaign-name" placeholder={popupMode ? "Untitled popup" : "Untitled campaign"}
            className="bg-slate-800/60 h-8 px-3 rounded-md text-[14px] font-semibold text-white placeholder:text-slate-500 focus:outline-none focus:bg-slate-800 w-52" />
          <div className="flex items-center gap-1">
            <button onClick={undo} disabled={!past.length} data-testid="eb-undo" className="h-8 w-8 rounded-md hover:bg-slate-800 flex items-center justify-center disabled:opacity-30"><Undo2 className="h-4 w-4" /></button>
            <button onClick={redo} disabled={!future.length} data-testid="eb-redo" className="h-8 w-8 rounded-md hover:bg-slate-800 flex items-center justify-center disabled:opacity-30"><Redo2 className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPreviewOpen(true)} data-testid="eb-preview" className="h-9 px-3 rounded-md text-[13px] font-medium hover:bg-slate-800 flex items-center gap-1.5"><Eye className="h-4 w-4" /> Preview</button>
          {!popupMode && (
            <>
              <button onClick={sendTest} disabled={testing} data-testid="eb-test" className="h-9 px-3 rounded-md text-[13px] font-medium hover:bg-slate-800 flex items-center gap-1.5">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test</button>
              <button onClick={() => setRecipientsOpen(true)} data-testid="eb-recipients" className="h-9 px-3 rounded-md text-[13px] font-medium hover:bg-slate-800 flex items-center gap-1.5">
                <Users className="h-4 w-4" /> Recipients
                <span className="ml-0.5 h-5 min-w-5 px-1 rounded-full bg-blue-600 text-white text-[11px] font-semibold flex items-center justify-center">{preview?.count ?? 0}</span>
              </button>
              <div className="w-px h-6 bg-slate-700 mx-0.5" />
              <button onClick={saveAsTemplate} disabled={savingTemplate} data-testid="eb-save-template" className="h-9 px-3 rounded-md text-[13px] font-medium hover:bg-slate-800 flex items-center gap-1.5">{savingTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bookmark className="h-4 w-4" />} Save as template</button>
              <button onClick={() => save(false)} disabled={saving} data-testid="campaign-save-draft" className="h-9 px-3 rounded-md text-[13px] font-medium hover:bg-slate-800 flex items-center gap-1.5"><Save className="h-4 w-4" /> Save draft</button>
              <Button onClick={() => save(true)} disabled={saving} data-testid="campaign-send-now" className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now</Button>
            </>
          )}
          {popupMode && (
            <>
              <div className="w-px h-6 bg-slate-700 mx-0.5" />
              <Button onClick={saveAsPopup} disabled={savingPopup} data-testid="eb-save-popup" className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white">{savingPopup ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save as Popup</Button>
            </>
          )}
        </div>
      </div>

      {/* Subject sub-bar (campaigns only — a popup has no subject line) */}
      {!popupMode && (
        <div className="h-11 bg-slate-800 border-t border-slate-700/60 flex items-center px-4 gap-3 shrink-0">
          <span className="text-[12px] font-medium text-slate-400 shrink-0">Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="campaign-subject" placeholder="Your subject line…"
            className="flex-1 max-w-2xl bg-slate-700/50 h-7 px-2.5 rounded text-[13px] text-white placeholder:text-slate-500 focus:outline-none focus:bg-slate-700" />
          <div className="flex items-center gap-1 min-w-0">
            {["firstName", "companyName"].map((t) => (
              <button key={t} onClick={() => setSubject((s) => s + `{{${t}}}`)} data-testid={`subject-merge-${t}`} className="h-6 px-1.5 rounded text-[10.5px] font-medium text-blue-300 bg-blue-500/15 hover:bg-blue-500/25 shrink-0">{`{{${t}}}`}</button>
            ))}
            <div className="w-px h-4 bg-slate-600 mx-0.5 shrink-0" />
            <div className="flex items-center gap-1 overflow-x-auto max-w-xs" data-testid="subject-merge-more">
              {MERGE_TAG_GROUPS.flatMap((g) => g.tags).filter((t) => t !== "firstName" && t !== "companyName").map((t) => (
                <button key={t} onClick={() => setSubject((s) => s + `{{${t}}}`)} data-testid={`subject-merge-${t}`} className="h-6 px-1.5 rounded text-[10.5px] font-medium text-blue-300 bg-blue-500/15 hover:bg-blue-500/25 shrink-0 whitespace-nowrap">{`{{${t}}}`}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        {/* Palette */}
        <div className="w-72 shrink-0 bg-slate-800 text-slate-200 flex flex-col border-t border-slate-700/60" data-testid="eb-palette">
          <div className="flex border-b border-slate-700">
            {[["blocks", "Blocks"], ["sections", "Sections"], ["templates", "Templates"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`palette-tab-${k}`} className={`flex-1 py-3 text-[12px] font-semibold ${tab === k ? "text-white border-b-2 border-blue-500" : "text-slate-400"}`}>{l}</button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            {tab === "blocks" ? (
              <>
                <div className="text-[10.5px] font-semibold text-slate-400 mb-1.5">LAYOUT</div>
                <div className="grid grid-cols-3 gap-1.5 mb-4">
                  {LAYOUTS.map((lay) => (
                    <button key={lay.cols} onClick={() => addRow(lay.cols)} data-testid={`palette-layout-${lay.cols}`} title={lay.label}
                      className="h-12 rounded-md bg-slate-700/60 hover:bg-slate-700 flex flex-col items-center justify-center gap-0.5 text-slate-300">
                      <lay.icon className="h-4 w-4" /><span className="text-[9px]">{lay.cols} col</span>
                    </button>
                  ))}
                </div>
                {popupMode && (
                  <>
                    <div className="text-[10.5px] font-semibold text-slate-400 mb-1.5">BASIC</div>
                    <div className="grid grid-cols-2 gap-1.5 mb-4">
                      {POPUP_BLOCK_DEFS.map((d) => (
                        <button key={d.type} draggable
                          onDragStart={(e) => { dragRef.current = { kind: "newblock", type: d.type }; e.dataTransfer.effectAllowed = "copy"; e.dataTransfer.setData("text/plain", d.type); }}
                          onDragEnd={() => { dragRef.current = null; }}
                          onClick={() => addBlock(d.type)} data-testid={`palette-block-${d.type}`}
                          className="h-16 rounded-md bg-slate-700/60 hover:bg-slate-700 cursor-grab flex flex-col items-center justify-center gap-1 text-slate-200">
                          <d.icon className="h-4 w-4" /><span className="text-[10.5px]">{d.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <div className="text-[10.5px] font-semibold text-slate-400 mb-1.5">BLOCKS</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {BLOCK_DEFS.map((d) => (
                    <button key={d.type} draggable
                      onDragStart={(e) => { dragRef.current = { kind: "newblock", type: d.type }; e.dataTransfer.effectAllowed = "copy"; e.dataTransfer.setData("text/plain", d.type); }}
                      onDragEnd={() => { dragRef.current = null; }}
                      onClick={() => addBlock(d.type)} data-testid={`palette-block-${d.type}`}
                      className="h-16 rounded-md bg-slate-700/60 hover:bg-slate-700 cursor-grab flex flex-col items-center justify-center gap-1 text-slate-200">
                      <d.icon className="h-4 w-4" /><span className="text-[10.5px]">{d.label}</span>
                    </button>
                  ))}
                </div>
                {reviewTemplate && (
                  <>
                    <div className="text-[10.5px] font-semibold text-slate-400 mb-1.5 mt-4">QUICK ADD</div>
                    <button onClick={() => loadTemplate(reviewTemplate)} data-testid="palette-block-review"
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md bg-slate-700/60 hover:bg-slate-700 text-[13px] text-slate-200 text-left">
                      <Sparkles className="h-4 w-4 text-blue-300" /> {reviewTemplate.name}
                    </button>
                  </>
                )}
              </>
            ) : tab === "sections" ? (
              <div className="space-y-1.5" data-testid="palette-sections">
                {sections.map((s) => (
                  <button key={s.id} onClick={() => addSection(s)} data-testid={`palette-section-${s.id}`}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md bg-slate-700/60 hover:bg-slate-700 text-[13px] text-slate-200 text-left">
                    <s.icon className="h-4 w-4 text-slate-300" /> {s.name}
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="palette-templates">
                <div className="text-[10.5px] font-semibold text-slate-400 mb-1">BUILT-IN</div>
                {templates.filter((t) => t.id !== "review").map((t) => (
                  <button key={t.id} onClick={() => loadTemplate(t)} data-testid={`campaign-template-${t.id}`}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md bg-slate-700/60 hover:bg-slate-700 text-[13px] text-slate-200 text-left">
                    <Sparkles className="h-4 w-4 text-blue-300" /> {t.name}
                  </button>
                ))}
                <div className="text-[10.5px] font-semibold text-slate-400 mb-1 mt-3">MY TEMPLATES</div>
                {customTemplates.length === 0 ? (
                  <p className="text-[11.5px] text-slate-500 px-1 leading-relaxed">Save any design with <span className="text-slate-300 font-medium">Save as template</span> to reuse it later.</p>
                ) : customTemplates.map((t) => (
                  <div key={t.id} data-testid={`custom-template-${t.id}`} className="group flex items-center gap-1.5 rounded-md bg-slate-700/60 hover:bg-slate-700">
                    <button onClick={() => loadTemplate(t)} data-testid={`custom-template-load-${t.id}`}
                      className="flex-1 flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-slate-200 text-left min-w-0">
                      <LayoutTemplate className="h-4 w-4 text-emerald-300 shrink-0" /> <span className="truncate">{t.name}</span>
                    </button>
                    <button onClick={() => deleteCustomTemplate(t)} data-testid={`custom-template-delete-${t.id}`}
                      className="h-7 w-7 mr-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-slate-600/60 flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-y-auto" style={{ background: design.settings.backgroundColor }} onClick={() => { setSel({ kind: "template" }); setPropTab("template"); }} data-testid="eb-canvas">
          <div className="mx-auto mt-8 mb-24" style={{ maxWidth: design.settings.contentWidth }} onClick={(e) => e.stopPropagation()}>
            <div className="rounded-xl shadow-sm overflow-hidden" style={{ background: design.settings.contentBackgroundColor, fontFamily: design.settings.fontFamily }}>
              {design.rows.length === 0 && (
                <div className="m-4 border-2 border-dashed border-slate-300 rounded-xl py-16 text-center text-slate-400 text-[13.5px]" data-testid="eb-empty">
                  Click a block or layout on the left to start building.
                </div>
              )}
              {design.rows.map((row, ri) => {
                const rowSel = sel.kind === "row" && sel.rowId === row.id;
                return (
                  <div key={row.id} data-testid={`email-row-${row.id}`}
                    onClick={(e) => { e.stopPropagation(); setSel({ kind: "row", rowId: row.id }); setPropTab("settings"); }}
                    className={`relative group/row px-2 ${rowSel ? "ring-2 ring-blue-300" : "hover:ring-1 hover:ring-slate-200"}`} style={{ background: row.bg || "transparent" }}>
                    {/* Row toolbar — floats ABOVE the block (negative top), rendered as a pill with white bg + shadow so it never blends into the email content. Visible when the row is selected OR the row is hovered. */}
                    <div className={`absolute right-2 -top-3 z-20 items-center gap-1 bg-white rounded-full shadow-lg ring-1 ring-slate-200 px-1 py-0.5 transition-opacity ${rowSel ? "flex opacity-100" : "hidden group-hover/row:flex opacity-0 group-hover/row:opacity-100"}`}>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 pl-1 pr-0.5">Section</span>
                      <button onClick={(e) => { e.stopPropagation(); moveRow(row.id, -1); }} disabled={ri === 0} data-testid={`row-up-${row.id}`} title="Move up" className="h-7 w-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 disabled:text-slate-300 disabled:cursor-not-allowed"><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); moveRow(row.id, 1); }} disabled={ri === design.rows.length - 1} data-testid={`row-down-${row.id}`} title="Move down" className="h-7 w-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 disabled:text-slate-300 disabled:cursor-not-allowed"><ArrowDown className="h-3.5 w-3.5" /></button>
                      <span className="h-4 w-px bg-slate-200 mx-0.5" />
                      <button onClick={(e) => { e.stopPropagation(); deleteRow(row.id); }} data-testid={`row-delete-${row.id}`} title="Delete section" className="h-7 w-7 rounded-full hover:bg-rose-50 flex items-center justify-center text-rose-500 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="flex">
                      {row.columns.map((col, ci) => (
                        <div key={ci} style={{ width: `${100 / row.cols}%` }} className="min-w-0 p-1.5"
                          data-testid={`email-col-${row.id}-${ci}`}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-blue-50"); }}
                          onDragLeave={(e) => e.currentTarget.classList.remove("bg-blue-50")}
                          onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("bg-blue-50"); dropBlockInColumn(row.id, ci); }}>
                          {col.length === 0 ? (
                            <div className="border border-dashed border-slate-300 rounded-md py-6 text-center text-[11.5px] text-slate-400" data-testid={`col-empty-${row.id}-${ci}`}>Drop a block</div>
                          ) : col.map((block, bi) => {
                            const bsel = sel.kind === "block" && sel.blockId === block.id;
                            return (
                              <div key={block.id} data-testid={`email-block-${block.id}`}
                                draggable
                                onDragStart={(e) => { dragRef.current = { kind: "moveblock", rowId: row.id, col: ci, blockId: block.id }; e.dataTransfer.effectAllowed = "move"; }}
                                onDragEnd={() => { dragRef.current = null; }}
                                onClick={(e) => { e.stopPropagation(); setSel({ kind: "block", rowId: row.id, colIndex: ci, blockId: block.id }); setPropTab("settings"); }}
                                className={`relative group/blk rounded-md cursor-pointer transition-all ${bsel ? "ring-2 ring-blue-500" : "hover:ring-1 hover:ring-blue-200"}`}>
                                {/* Block toolbar — same floating-pill pattern, offset a bit further right so it doesn't overlap the row toolbar */}
                                <div className={`absolute right-1 -top-3 z-20 items-center gap-1 bg-white rounded-full shadow-lg ring-1 ring-blue-200 px-1 py-0.5 transition-opacity ${bsel ? "flex opacity-100" : "hidden group-hover/blk:flex opacity-0 group-hover/blk:opacity-100"}`}>
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-500 pl-1 pr-0.5">Block</span>
                                  <button onClick={(e) => { e.stopPropagation(); moveBlock(row.id, ci, block.id, -1); }} disabled={bi === 0} data-testid={`block-up-${block.id}`} title="Move up" className="h-7 w-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 disabled:text-slate-300 disabled:cursor-not-allowed"><ArrowUp className="h-3.5 w-3.5" /></button>
                                  <button onClick={(e) => { e.stopPropagation(); moveBlock(row.id, ci, block.id, 1); }} disabled={bi === col.length - 1} data-testid={`block-down-${block.id}`} title="Move down" className="h-7 w-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 disabled:text-slate-300 disabled:cursor-not-allowed"><ArrowDown className="h-3.5 w-3.5" /></button>
                                  <button onClick={(e) => { e.stopPropagation(); dupBlock(row.id, ci, block.id); }} data-testid={`block-dup-${block.id}`} title="Duplicate" className="h-7 w-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600"><Copy className="h-3.5 w-3.5" /></button>
                                  <span className="h-4 w-px bg-slate-200 mx-0.5" />
                                  <button onClick={(e) => { e.stopPropagation(); deleteBlock(row.id, ci, block.id); }} data-testid={`block-delete-${block.id}`} title="Delete block" className="h-7 w-7 rounded-full hover:bg-rose-50 flex items-center justify-center text-rose-500 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                                <div style={{ pointerEvents: "none" }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderBlockPreview(block)) }} />
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right panel */}
        <div className="w-80 shrink-0 bg-white border-l border-slate-200 overflow-y-auto p-5" data-testid="eb-props">
          <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-1">
            <button onClick={() => setPropTab("settings")} data-testid="eb-proptab-settings"
              className={`flex-1 h-7 rounded-md text-[12px] font-medium transition-colors ${propTab === "settings" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Settings</button>
            <button onClick={() => setPropTab("template")} data-testid="eb-proptab-template"
              className={`flex-1 h-7 rounded-md text-[12px] font-medium transition-colors ${propTab === "template" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Template</button>
          </div>
          {propTab === "template" ? (
            <div className="space-y-3" data-testid="eb-template-settings">
              <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5"><SettingsIcon className="h-4 w-4 text-slate-400" /> Template settings</div>
              <Field label="Background color"><Color value={design.settings.backgroundColor} onChange={(c) => setSettings({ backgroundColor: c })} testid="ts-bg" /></Field>
              <Field label="Content background color"><Color value={design.settings.contentBackgroundColor} onChange={(c) => setSettings({ contentBackgroundColor: c })} testid="ts-content-bg" /></Field>
              <Field label="Content width (px)"><input type="number" min="400" max="800" value={design.settings.contentWidth} onChange={(e) => setSettings({ contentWidth: Number(e.target.value) })} data-testid="ts-width" className={txtInput} /></Field>
              <Field label="Font"><select value={design.settings.fontFamily} onChange={(e) => setSettings({ fontFamily: e.target.value })} data-testid="ts-font" className={`${txtInput} bg-white`}>
                <option value="Helvetica, Arial, sans-serif">Helvetica / Arial</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="'Trebuchet MS', sans-serif">Trebuchet</option>
                <option value="'Courier New', monospace">Courier</option>
              </select></Field>
              <Field label="Preheader text"><input value={design.settings.preheader} onChange={(e) => setSettings({ preheader: e.target.value })} data-testid="ts-preheader" placeholder="Inbox preview line…" className={txtInput} /></Field>
              <p className="text-[11px] text-slate-400">The preheader is the short summary shown after the subject in most inboxes.</p>
              <div className="pt-2 border-t border-slate-100">
                <button onClick={saveAsTemplate} disabled={savingTemplate} data-testid="ts-save-template"
                  className="w-full h-9 rounded-md text-[13px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 flex items-center justify-center gap-1.5">
                  {savingTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bookmark className="h-4 w-4" />} Save as template
                </button>
              </div>
            </div>
          ) : selBlock ? (
            <>
              <div className="text-[11px] font-semibold tracking-wide text-slate-400 uppercase mb-3">{selBlock.type} block</div>
              <BlockSettings block={selBlock} accent={accent} onChange={(next) => updateBlock(sel.rowId, sel.colIndex, sel.blockId, next)} onUpload={openUpload} />
            </>
          ) : selRow ? (
            <div className="space-y-3" data-testid="eb-row-settings">
              <div className="text-[11px] font-semibold tracking-wide text-slate-400 uppercase">Row</div>
              <Field label="Columns">
                <div className="flex gap-1.5">{[1, 2, 3].map((c) => <button key={c} onClick={() => setRowCols(selRow.id, c)} data-testid={`row-cols-${c}`} className={`flex-1 h-8 rounded-md text-[12px] border ${selRow.cols === c ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{c}</button>)}</div>
              </Field>
              <Field label="Background color"><Color value={selRow.bg} onChange={(c) => updateRow(selRow.id, { bg: c })} /></Field>
              <button onClick={() => deleteRow(selRow.id)} className="w-full h-9 rounded-md text-[13px] font-medium text-red-600 border border-red-200 hover:bg-red-50 flex items-center justify-center gap-1.5"><Trash2 className="h-4 w-4" /> Delete row</button>
            </div>
          ) : (
            <div className="text-[13px] text-slate-400 leading-relaxed py-6 text-center" data-testid="eb-no-selection">
              Click a block or row on the canvas to edit it, or switch to the <button onClick={() => setPropTab("template")} className="text-blue-600 font-medium">Template</button> tab.
            </div>
          )}
        </div>
      </div>

      {/* Recipients drawer */}
      {recipientsOpen && (
        <div className="fixed inset-0 z-[125] flex justify-end" data-testid="recipients-drawer">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setRecipientsOpen(false)} />
          <div className="relative w-full max-w-[460px] bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between">
              <div>
                <h2 className="text-[15px] font-semibold text-slate-900 flex items-center gap-1.5"><Users className="h-4 w-4 text-blue-600" /> Recipients</h2>
                <p className="text-[12px] text-slate-500">Choose who receives this campaign.</p>
              </div>
              <button onClick={() => setRecipientsOpen(false)} data-testid="recipients-close" className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"><X className="h-4.5 w-4.5 text-slate-500" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 flex items-center justify-between" data-testid="audience-count">
                <span className="text-[13px] text-slate-600">Matching customers</span>
                <span className="text-[18px] font-semibold text-blue-700 flex items-center gap-1.5">{previewing ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (preview?.count ?? 0)}<span className="text-[12px] font-normal text-slate-400">of {preview?.total ?? 0} · with email</span></span>
              </div>
              <div><div className="text-[11px] text-slate-500 mb-1.5">Customer type</div><div className="flex gap-2">{TYPES.map((t) => <AudienceChip key={t} active={crit.types.includes(t)} onClick={() => toggleCrit("types", t)} testid={`audience-type-${t}`}>{t}</AudienceChip>)}</div></div>
              <div><div className="text-[11px] text-slate-500 mb-1.5">Membership</div><div className="flex gap-2">{MEMBERSHIPS.map((m) => <AudienceChip key={m} active={crit.memberships.includes(m)} onClick={() => toggleCrit("memberships", m)} testid={`audience-membership-${m}`}>{m}</AudienceChip>)}</div></div>
              {tagOptions.length > 0 && <div><div className="text-[11px] text-slate-500 mb-1.5">Has any of these tags</div><div className="flex flex-wrap gap-2">{tagOptions.map((t) => <AudienceChip key={t} active={crit.tags.includes(t)} onClick={() => toggleCrit("tags", t)} testid={`audience-tag-${t}`}>{t}</AudienceChip>)}</div></div>}
              <div className="grid grid-cols-3 gap-3 pt-1">
                <div><div className="text-[11px] text-slate-500 mb-1">Min lifetime value</div><input type="number" min="0" value={crit.minLtv} onChange={(e) => setCrit((c) => ({ ...c, minLtv: e.target.value }))} data-testid="audience-min-ltv" className={txtInput} /></div>
                <div><div className="text-[11px] text-slate-500 mb-1">Served in last (days)</div><input type="number" min="0" value={crit.servedWithinDays} onChange={(e) => setCrit((c) => ({ ...c, servedWithinDays: e.target.value }))} data-testid="audience-served-within" placeholder="any" className={txtInput} /></div>
                <div><div className="text-[11px] text-slate-500 mb-1">Inactive ≥ (days)</div><input type="number" min="0" value={crit.inactiveDays} onChange={(e) => setCrit((c) => ({ ...c, inactiveDays: e.target.value }))} data-testid="audience-inactive" placeholder="any" className={txtInput} /></div>
              </div>
              {preview?.sample?.length > 0 && <div className="text-[11.5px] text-slate-500">e.g. {preview.sample.slice(0, 4).map((s) => s.name).join(", ")}{preview.count > 4 ? ` +${preview.count - 4} more` : ""}</div>}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex justify-end">
              <Button onClick={() => setRecipientsOpen(false)} data-testid="recipients-done" className="h-9 bg-blue-600 hover:bg-blue-700 text-white">Done</Button>
            </div>
          </div>
        </div>
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-[130] bg-slate-900/60 flex items-center justify-center p-6" onClick={() => setPreviewOpen(false)} data-testid="eb-preview-modal">
          <div className="bg-white rounded-xl w-full max-w-[680px] max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="text-[14px] font-semibold text-slate-800">Email preview {subject ? `· ${subject}` : ""}</div>
              <button onClick={() => setPreviewOpen(false)} data-testid="eb-preview-close" className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"><X className="h-4.5 w-4.5 text-slate-500" /></button>
            </div>
            <iframe title="email-preview" srcDoc={fillSample(popupMode ? renderPopupHtml(design) : renderEmailHtml(design))} className="flex-1 w-full" />
          </div>
        </div>
      )}
    </div>
  );
}
